import { useRef, useState } from "react";

import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
];
const MAX_BYTES = 15 * 1024 * 1024;

/**
 * The lock screen. The passport itself is the password — the payer never types
 * a number, which means a forwarded link is useless without the document.
 */
export default function PassportUpload({
  t,
  payerFirstName,
  attemptsRemaining,
  busy,
  progress,
  error,
  onUpload,
  onDismissError,
}) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [localError, setLocalError] = useState(null);

  const handleFile = (file) => {
    if (!file) return;

    // Some Android browsers report an empty type for HEIC — fall back to the
    // extension rather than blocking a valid upload.
    const extension = file.name?.split(".").pop()?.toLowerCase();
    const looksAccepted =
      ACCEPTED_TYPES.includes(file.type) ||
      ["jpg", "jpeg", "png", "webp", "heic", "heif", "pdf"].includes(extension);

    if (!looksAccepted) {
      setLocalError(t("fileWrongType"));
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError(t("fileTooLarge"));
      return;
    }

    setLocalError(null);
    onDismissError?.();
    onUpload(file);
  };

  const shownError = localError || error;

  return (
    <Stack spacing={3}>
      <Stack spacing={1.5} alignItems="center" sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(241,94,34,0.1)",
            color: "primary.main",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 30 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#0e3461" }}>
          {t("unlockHeading")}
        </Typography>
        {payerFirstName && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("unlockGreeting", { name: payerFirstName })}
          </Typography>
        )}
      </Stack>

      <Typography
        variant="body1"
        sx={{ color: "text.secondary", textAlign: "center", fontSize: 15 }}
      >
        {t("unlockIntro")}
      </Typography>

      <Alert
        severity="warning"
        icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
        sx={{ borderRadius: 2, alignItems: "flex-start" }}
      >
        <AlertTitle sx={{ fontSize: 14, fontWeight: 700, mb: 0.25 }}>
          {t("unlockWarningTitle")}
        </AlertTitle>
        <Typography variant="body2" sx={{ fontSize: 13.5 }}>
          {t("unlockWarningBody")}
        </Typography>
      </Alert>

      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          bgcolor: "#f6f7fb",
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <BadgeOutlinedIcon sx={{ fontSize: 18, color: "#0e3461" }} />
          <Typography variant="subtitle2" sx={{ color: "#0e3461" }}>
            {t("unlockChecklistTitle")}
          </Typography>
        </Stack>
        <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
          {[t("unlockCheck1"), t("unlockCheck2"), t("unlockCheck3")].map(
            (line) => (
              <Typography
                key={line}
                component="li"
                variant="body2"
                sx={{ color: "text.secondary", fontSize: 13.5 }}
              >
                {line}
              </Typography>
            )
          )}
        </Stack>
      </Box>

      {shownError && (
        <Alert
          severity="error"
          sx={{ borderRadius: 2 }}
          onClose={() => {
            setLocalError(null);
            onDismissError?.();
          }}
        >
          {shownError}
          {attemptsRemaining != null && attemptsRemaining > 0 && (
            <Box sx={{ mt: 0.5, fontWeight: 600 }}>
              {t("unlockAttempts", { count: attemptsRemaining })}
            </Box>
          )}
        </Alert>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,.heic,.heif"
        hidden
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />

      {busy ? (
        <Stack spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={34} />
          <Typography variant="subtitle2">{t("unlockChecking")}</Typography>
          {progress > 0 && progress < 100 && (
            <Box sx={{ width: "100%", maxWidth: 280 }}>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ py: 1.5, borderRadius: 2, textTransform: "none", fontSize: 16 }}
          >
            {t("unlockCta")}
          </Button>
          <Button
            fullWidth
            size="large"
            variant="outlined"
            color="secondary"
            startIcon={<PhotoCameraOutlinedIcon />}
            onClick={() => cameraInputRef.current?.click()}
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 16,
              display: { xs: "flex", md: "none" },
            }}
          >
            {t("unlockCtaCamera")}
          </Button>
        </Stack>
      )}

      <Typography
        variant="caption"
        sx={{ color: "text.disabled", textAlign: "center", lineHeight: 1.5 }}
      >
        {t("unlockPrivacy")}
      </Typography>
    </Stack>
  );
}
